import { UI, Stack, VStack } from '@genial/components';
import { BoxBorder } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, FlatList } from 'native-base';
import React from 'react';
import { useRecoilState } from 'recoil';

import { useFilterTransformer } from '../../../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { subscriptionsFiltersAtom } from '../../../../store/subscription/atoms';
import { subscriptionListQuery } from '../../../../store/subscription/selector';
import { SubscriptionItemProps } from '../../../../store/subscription/types';
import { SubscriptionCard } from '../SubscriptionCard';
import { SubscriptionCardLoading } from '../SubscriptionCardLoading';
import { SubscriptionEmptyList } from '../SubscriptionEmptyList';
import { SubscriptionFilters } from '../SubscriptionFilters';

const ItemSeparator = () => <Box height="stack.nano" />;

interface SubscriptionListProps {
  isMobile: boolean;
}

export const SubscriptionList: React.FC<SubscriptionListProps> = ({ isMobile }) => {
  const [{ appliedFilters, availableFilters }] = useRecoilState(subscriptionsFiltersAtom);
  const { transformFilters } = useFilterTransformer(availableFilters);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const filtersToApply = transformFilters(appliedFilters, { transformRangeFieldNames: true });

  const { data, loading, error, refresher } = useRecoilQuery(
    subscriptionListQuery({ filtersToApply })
  );

  const handleOpenDetails = (subscription: SubscriptionItemProps) => {
    navigate('SubscriptionDetails', { subscription });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <FlatList
          data={Array.from({ length: 8 })}
          showsVerticalScrollIndicator={false}
          ItemSeparatorComponent={ItemSeparator}
          keyExtractor={(_, index) => String(index)}
          renderItem={() => <SubscriptionCardLoading />}
        />
      );
    }

    if (error) {
      return <UI.ErrorBoundary onRefresh={refresher} />;
    }

    return (
      <FlatList
        keyExtractor={({ baseIsinCode }, index) => `${baseIsinCode}-${index}`}
        windowSize={20}
        maxToRenderPerBatch={20}
        updateCellsBatchingPeriod={5}
        data={data?.data}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={1.5}
        ItemSeparatorComponent={ItemSeparator}
        renderItem={({ item }) => (
          <SubscriptionCard subscription={item} openDetails={() => handleOpenDetails(item)} />
        )}
        ListEmptyComponent={<SubscriptionEmptyList />}
      />
    );
  };

  const hasDataAndMoreThanOneItem = !!data?.data && (data?.data?.length || 0) > 1;

  return (
    <VStack
      w="full"
      bg={Utils.Responsive.PhoneTablet({ tablet: 'neutral.white', phone: undefined })}
      borderRadius={BoxBorder.heavy}
      p={Utils.Responsive.All({ desktop: 'inset.md', tablet: 'inset.sm', phone: 0 })}
      space="stack.xxs">
      <Stack zIndex={5}>
        <SubscriptionFilters isMobile={isMobile} />
      </Stack>
      <UI.Shadow
        level={isMobile || hasDataAndMoreThanOneItem ? 'none' : 1}
        borderRadius={BoxBorder.heavy}>
        {renderContent()}
      </UI.Shadow>
    </VStack>
  );
};
